/**
 * The public marketing pages, in every language they are published in.
 *
 * SSoT for three things that would otherwise drift apart:
 *   1. the router table            (src/router/routes.js)
 *   2. the language switch and
 *      in-page navigation          (src/mixins/websiteLanguageMixin.js)
 *   3. the crawlable static shells,
 *      sitemap.xml and llms.txt    (scripts/generate-seo.js)
 *
 * Adding a public page means adding ONE entry here. Forgetting any of the three
 * consumers then becomes impossible rather than merely unlikely.
 *
 * WHY THE URL CARRIES THE LANGUAGE
 * --------------------------------
 * `/about` is Hebrew, `/en/about` is English, and each is separately indexable,
 * linkable and shareable. A language held only in localStorage is invisible to
 * Google, to Bing and to the fetchers behind ChatGPT and Perplexity: they would
 * only ever see Hebrew, and the entire English translation would never rank.
 *
 * The signed-in app deliberately does NOT work this way - it has no SEO surface
 * and no reason to carry a language in its URLs, so it keeps using the stored
 * preference.
 *
 * CommonJS on purpose: `scripts/generate-seo.js` is a plain Node build script
 * and cannot import an ES module, while webpack reads CommonJS without help.
 */

const LOCALES = ['he', 'en'];
const DEFAULT_LOCALE = 'he';
const RTL_LOCALES = ['he', 'ar'];

const PUBLIC_PAGES = [
  {
    key: 'home',
    paths: { he: '/', en: '/en/' },
    routeNames: { he: 'website-home', en: 'website-home-en' },
    titleKey: 'pages.website.seo.title',
    // Where the generated shell lands inside dist/ (used by generate-seo.js).
    files: { he: 'index.html', en: 'en/index.html' },
  },
  {
    key: 'about',
    paths: { he: '/about', en: '/en/about' },
    routeNames: { he: 'about-us', en: 'about-us-en' },
    titleKey: 'pages.website.about.meta_title',
    files: { he: 'about.html', en: 'en/about.html' },
  },
  {
    key: 'contact',
    paths: { he: '/contact', en: '/en/contact' },
    routeNames: { he: 'contact-us', en: 'contact-us-en' },
    titleKey: 'pages.website.contact.meta_title',
    files: { he: 'contact.html', en: 'en/contact.html' },
  },
];

/** Static legal documents: indexable, but not generated from locale copy. */
const LEGAL_PATHS = ['/terms.html', '/privacy.html', '/refund.html'];

/**
 * The FAQ entries, in order.
 *
 * Shared by the rendered accordion on the home page, the static shell and the
 * FAQPage structured data. When the component kept its own list, adding a
 * question to the locale files showed it to crawlers and to assistants but not
 * to the person actually reading the page.
 */
const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10'];

/**
 * How each language names itself, as a locale key. Used by the language switch
 * in the app and by the static shells, so both always offer the same wording.
 */
const LOCALE_LABEL_KEYS = {
  he: 'pages.website.language_switcher.hebrew',
  en: 'pages.website.language_switcher.english',
};

function isRtl(locale) {
  return RTL_LOCALES.indexOf(locale) !== -1;
}

/** The page whose route name is `routeName`, in any locale, or undefined. */
function pageByRouteName(routeName) {
  return PUBLIC_PAGES.find((page) =>
    LOCALES.some((locale) => page.routeNames[locale] === routeName)
  );
}

/** The route name for `key` in `locale`, falling back to the default locale. */
function routeNameFor(key, locale) {
  const page = PUBLIC_PAGES.find((p) => p.key === key);
  if (!page) return null;
  return page.routeNames[locale] || page.routeNames[DEFAULT_LOCALE];
}

module.exports = {
  LOCALES,
  DEFAULT_LOCALE,
  RTL_LOCALES,
  PUBLIC_PAGES,
  LEGAL_PATHS,
  FAQ_KEYS,
  LOCALE_LABEL_KEYS,
  isRtl,
  pageByRouteName,
  routeNameFor,
};
